import React from "react";

import ace from "ace-builds";
import "ace-builds/src-noconflict/keybinding-sublime";
import "ace-builds/src-noconflict/ext-prompt";
import "ace-builds/src-noconflict/ext-searchbox";
import { StatusBar } from "ace-builds/src-noconflict/ext-statusbar";

import "./use-editor.scss";

type RemoveListenerFn = () => void;
type ListenerFn<P> = (params: P) => void;
type AddListenerFn<P> = (listener: ListenerFn<P>) => RemoveListenerFn;

type OnChange = AddListenerFn<{ value: string }>;

export interface EditorIndent {
    tab: boolean;
    width: number;
}

export interface EditorCommand {
    name: string;
    key: { mac: string; win: string };
    fn: () => void;
}

type AddCommand = (command: EditorCommand) => () => void;

interface EditorParams {
    config: Partial<ace.Ace.EditorOptions>;
    removeCommands: string[];
    statusVisible?: boolean;
}

export interface EditorManager {
    params: EditorParams;
    getValue: () => string | null;
    setValue: (value: string) => void;
    setIndent: (indent: EditorIndent) => void;
    onChange: OnChange;
    addCommand: AddCommand;
    refBody: React.RefObject<HTMLDivElement>;
    refStatus: React.RefObject<HTMLDivElement>;
}

type UseEditor = (params: EditorParams) => EditorManager;

const baseConfig: Partial<ace.Ace.EditorOptions> = {
    showInvisibles: true,
    displayIndentGuides: false,
    printMargin: false,
    keyboardHandler: "ace/keyboard/sublime",
    wrap: false as any, // https://github.com/ajaxorg/ace/pull/4292
};

export const useEditor: UseEditor = (params) => {
    const refBody = React.useRef<HTMLDivElement>(null);
    const refStatus = React.useRef<HTMLDivElement>(null);
    const [editor, setEditor] = React.useState<ace.Ace.Editor | null>(null);

    // init ace
    React.useEffect(() => {
        if (editor !== null) return;

        if (refBody.current === null) throw Error("Body is not defined.");
        const config = { ...baseConfig, ...params.config };
        const _editor = ace.edit(refBody.current, config);

        if (params.statusVisible) {
            const statusElm = refStatus.current;
            if (statusElm === null) throw Error("Status is not defined");
            const statusBar = new StatusBar(_editor, refStatus.current);
            statusBar.element.textContent = "0:0";
        }

        _editor.focus();
        setEditor(_editor);
    }, [editor, params.config]);

    React.useEffect(() => {
        if (editor === null) return;
        params.removeCommands.forEach((command) => {
            // https://github.com/ajaxorg/ace/pull/4293
            editor.commands.removeCommand(command as any);
        });
    }, [editor, params.removeCommands]);

    // event listeners
    const onChange: OnChange = React.useCallback(
        (listener) => {
            if (editor === null) return () => {};
            const cb = () => listener({ value: editor.getValue() });
            editor.on("change", cb);
            return () => editor.off("change", cb);
        },
        [editor]
    );

    const addCommand: AddCommand = React.useCallback(
        (command) => {
            if (editor === null) return () => {};
            const aceCommand: ace.Ace.Command = {
                bindKey: command.key,
                name: command.name,
                exec: command.fn,
            };
            editor.commands.addCommand(aceCommand);
            return () => editor.commands.removeCommand(aceCommand);
        },
        [editor]
    );

    const getValue = React.useCallback(() => {
        return editor?.getValue() ?? null;
    }, [editor]);

    const setValue = React.useCallback(
        (value: string) => {
            if (editor === null) return;
            editor.setValue(value);
            editor.navigateFileEnd();
            window.setTimeout(() => editor.scrollToRow(Infinity), 0);
        },
        [editor]
    );

    const setIndent = React.useCallback(
        (indent: EditorIndent) => {
            if (editor === null) return;
            editor.session.setUseSoftTabs(!indent.tab);
            editor.session.setTabSize(indent.width);
        },
        [editor]
    );

    const ops = { getValue, setValue, setIndent, onChange, addCommand };
    const refs = { refBody, refStatus };
    return { params, ...ops, ...refs };
};
